/**
 * What a reader waited for, reported back to `POST /timings`: each gesture until it showed and
 * until the server's answer was drawn, the page load from DNS to first content, and each
 * request the page made with the `Server-Timing` it carried. The server stamps the rows with
 * its own minute and the reader's kind, so nothing here says who is asking or when.
 *
 * **Nothing here can delay or break a click.** A gesture only reads the clock and asks for a
 * frame; everything else waits for the minute's flush or for the page to be hidden, and goes
 * by `sendBeacon`, which never blocks. `requestAnimationFrame`, `sendBeacon`, the navigation
 * entry and `PerformanceObserver` are each called inside a `try`, so a browser without one of
 * them reports less and draws the same. No layout is read.
 *
 * A gesture is timed from the click that made it: a capture listener on the window runs
 * before any handler, so it reads the clock as the click is dispatched. Opening the briefing
 * or an event draws from an effect a render later, so the click has to be remembered until
 * then. A Back or Forward clears it, since nothing a reader pressed made that navigation.
 */

/** The gestures an action may name: the server's `timings.ACTIONS`, which a test holds equal. */
export const ACTIONS = [
  'save',
  'going',
  'favourite',
  'star',
  'friend',
  'done',
  'brief',
  'event',
  'month',
  'search',
] as const
export type Action = (typeof ACTIONS)[number]
/**
 * What a gesture's caller says once the answer is drawn. `late` says the reader saw nothing
 * worth the name before then, so shown is that same frame.
 */
export type Settle = (ok: boolean, late?: boolean) => void

const PATH = '/timings'
const MINUTE = 6e4

type Item = Record<string, string | number | boolean | undefined>

let held: Item[] = []
let sent: number[] = []
/** When the last click was dispatched, until a Back or Forward. */
let at: number | undefined
/**
 * How many times the page has been hidden, a load that began hidden counting as once: a frame
 * that comes after one is not a wait.
 */
let hid = 0
/**
 * When the app first drew a page from server data, from the navigation's own zero. Absent if
 * the page was hidden at any point before it, when it would time the reader's attention.
 */
let first: number | undefined
let loaded = false

const now = () => performance.now()

function frame(then: () => void): void {
  try {
    requestAnimationFrame(then)
  } catch {
    // No frame, so no number: an action that never painted is not sent.
  }
}

/**
 * Start timing a gesture called `name`, from `began`: the last click unless the caller knows
 * better. A gesture with nothing to time it from, or a name outside `ACTIONS`, has no shown
 * frame, and an action with none is never sent.
 *
 * Shown is the first frame after this call, which for a write is the one that draws the patch
 * its caller set before sending; settled is the frame after the caller's `Settle`. A frame that comes only after the
 * page was hidden is a time the tab spent in the background, so that action is dropped.
 */
export function act(name: Action, began = at): Settle {
  const hides = hid
  let shown: number | undefined
  if (began != null && ACTIONS.includes(name))
    frame(() => {
      shown = now() - began
    })
  return (ok, late) =>
    frame(() => {
      const settled = now() - began!
      if (hides === hid && shown != null)
        held.push({ k: 'a', name, ok, shown: late ? settled : shown, settled })
    })
}

/**
 * The app has drawn from an answer. The first frame after the first one is `first`, unless the
 * page has been hidden by then. Read in the frame, since a hide can come between the two.
 */
export function drew(): void {
  if (first == null)
    frame(() => {
      if (!hid) first ??= now()
    })
}

/**
 * `item` with the `wait`, `held` and `app` its entry's `Server-Timing` names. A figure left
 * `undefined` anywhere in an item is left out of the body by `JSON.stringify`, which is how a
 * phase that has not happened is absent rather than zero.
 */
function timed(item: Item, entry: PerformanceResourceTiming): Item {
  for (const { name, duration } of entry.serverTiming ?? [])
    if (/^(wait|held|app)$/.test(name)) item[name] = duration
  return item
}

function page(): void {
  const e = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
  const { connectStart, connectEnd, secureConnectionStart } = e
  // A connection the page did not open has no span to open it in.
  const reused = connectEnd === connectStart
  held.push(
    timed(
      {
        k: 'n',
        dns: e.domainLookupEnd - e.domainLookupStart,
        connect: connectEnd - connectStart,
        tls: reused || !secureConnectionStart ? 0 : connectEnd - secureConnectionStart,
        ttfb: e.responseStart,
        proto: e.nextHopProtocol,
        reused,
        dom: e.domContentLoadedEventEnd || undefined,
        load: e.loadEventEnd || undefined,
        first,
      },
      e,
    ),
  )
}

/**
 * The page's own requests as the observer hands them over. An observer rather than
 * `getEntriesByType`, because the timeline's buffer stops taking entries once it is full,
 * and an observer is handed every entry whether the buffer has room or not, pictures and
 * all. A cached answer is left out: it carries the headers of the response that filled the
 * cache. So is the beacon's own address, whatever the browser calls its initiator, or each
 * flush would report the one before it.
 */
function watch(list: PerformanceObserverEntryList): void {
  for (const e of list.getEntries() as PerformanceResourceTiming[]) {
    const { origin, pathname } = new URL(e.name)
    if (
      origin === location.origin &&
      /^(fetch|xmlhttprequest)$/.test(e.initiatorType) &&
      e.transferSize > 0 &&
      pathname !== PATH
    )
      held.push(timed({ k: 'r', path: pathname, dur: e.duration }, e))
  }
}

/**
 * The oldest items held that fit one body, taken off the list, within the server's caps of
 * 8,192 bytes and 50 items: every string in an item is ASCII, so a length is a size in bytes.
 * An item too big for a body on its own is dropped, so it cannot hold up the ones behind it.
 */
function body(): string | undefined {
  const items = held.splice(0, 50)
  let text
  while ((text = JSON.stringify({ v: 1, items })).length > 8192) held.unshift(items.pop()!)
  if (items.length) return text
  held.shift()
}

/**
 * Send what is held: one body on the minute, and as many as the server's limit of six a
 * minute allows when the page is hidden. What is past that limit waits for the next send it
 * allows, and is lost if the page closes first. A body `sendBeacon` refuses is dropped and
 * never retried. Each send first drops all but the newest 300 items held. Between sends
 * nothing bounds them.
 */
function flush(all?: boolean): void {
  if (!loaded)
    try {
      loaded = true
      page()
    } catch {
      // No navigation timing: the requests and the gestures are still worth sending.
    }
  try {
    const t = now()
    sent = sent.filter(s => t - s < MINUTE)
    held = held.slice(-300)
    while (held.length && sent.length < 6) {
      const one = body()
      if (one) {
        sent.push(t)
        navigator.sendBeacon(PATH, one)
      }
      if (!all) break
    }
  } catch {
    // No `sendBeacon`: what was taken is gone, which is what a refusal costs anyway.
  }
}

/** Start reporting. Called once, and never on the card headless Chrome draws. */
export function start(): void {
  try {
    // A gesture compares `hid` with the count when it began, so counting a hidden start
    // drops no gesture begun after it.
    if (document.hidden) hid++
    addEventListener('click', () => (at = now()), true)
    addEventListener('popstate', () => (at = undefined))
    const hide = () => {
      hid++
      flush(true)
    }
    // Both fire on one exit; the second finds nothing held.
    document.addEventListener('visibilitychange', () => document.hidden && hide())
    addEventListener('pagehide', hide)
    setInterval(() => flush(), MINUTE)
    new PerformanceObserver(watch).observe({ type: 'resource', buffered: true })
  } catch {
    // No observer: the gestures and the page load are still reported.
  }
}
